import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getAccessToken, 
  setAccessToken, 
  getUser, 
  setUser, 
  clearAccessToken, 
  clearUser,
} from '../../../lib/auth/tokenService';
import { sendOtp, verifyOtp, fetchPermissions, logout as apiLogout } from '../api/auth.api';
import { AuthState, LoginResponse, MenuPermission } from '../types';

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    permissions: [],
    isAuthenticated: false,
    isLoading: true,
  });

  const hasInitialized = useRef(false);

  // Initialize auth state
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initAuth = async () => {
      try {
        const token = getAccessToken();
        const user = getUser();
        
        if (token && user) {
          // Fetch permissions before setting state
          let permissions: MenuPermission[] = [];
          try {
            permissions = await fetchPermissions();
          } catch (error) {
            console.error('Failed to fetch permissions:', error);
          }

          // Set complete state once with user and permissions
          setState({
            user,
            permissions,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setState({
            user: null,
            permissions: [],
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setState({
          user: null,
          permissions: [],
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    initAuth();
  }, []);

  // Listen for session expiration events from httpClient
  useEffect(() => {
    const handleSessionExpired = () => {
      setState({
        user: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
      });
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  const login = useCallback(async (loginResponse: LoginResponse) => {
    const { access_token, user } = loginResponse;
    
    // Store tokens and user data
    setAccessToken(access_token);
    setUser(user);
    
    // Fetch permissions before setting state
    let permissions: MenuPermission[] = [];
    try {
      permissions = await fetchPermissions();
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }

    // Set complete state once with user and permissions
    setState({
      user,
      permissions,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setState({
        user: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const sendOtpCode = useCallback(async (mobileNo: string) => {
    return await sendOtp(mobileNo);
  }, []);

  const verifyOtpCode = useCallback(async (sessionId: string, otp: string) => {
    const loginResponse = await verifyOtp(sessionId, otp);
    await login(loginResponse);
    return loginResponse;
  }, [login]);

  return {
    ...state,
    login,
    logout,
    sendOtp: sendOtpCode,
    verifyOtp: verifyOtpCode,
  };
};